import type { Message } from '../../../core/api/generated/models/Message';
import type { MessageAttachment } from '../../../core/api/generated/models/MessageAttachment';
import type { MessageSenderGroup, ThreadAttachment, ThreadMessage, ThreadMessageButton, ThreadQuotedReply, ThreadStoryContext } from '../models/conversation-view.types';

/**
 * Mirrors the API's `MessageReplyTo` schema (see openapi/components/schemas/conversations).
 * The generated `Message` model doesn't declare this field yet — `npm run api:generate` picks
 * it up once run; reading it defensively here (present at runtime already) unblocks the UI
 * without waiting on that regeneration.
 */
interface RawMessageReplyTo {
  unavailable: boolean;
  messageId?: string;
  senderType?: Message['senderType'];
  preview?: string;
}

export function mapMessageToThread(message: Message): ThreadMessage {
  const metadata = message.contentMetadata ?? {};
  const isEcho = metadata['isEcho'] === true;
  // Disparo automático: mensagem outbound REAL enviada ao contato (não é evento de sistema).
  // Renderiza no lado de saída, como qualquer mensagem enviada, com um selo pela origem:
  // 'campaign_dispatch' → "Campanha" (marketing); 'webhook' → "Webhook" (gatilho de webhook).
  const isCampaign = metadata['source'] === 'campaign_dispatch';
  const isWebhook = metadata['source'] === 'webhook';
  const isDispatch = isCampaign || isWebhook;
  // Clique de botão/quick-reply ou resposta interativa (button_reply/list_reply): o rótulo veio
  // do botão, não foi digitado. NÃO usar só a presença de `interactive` — uma mensagem OUTBOUND
  // interativa que NÓS enviamos também carrega `interactive: {body, buttons}`, sem essas chaves.
  const inboundInteractive = asRecord(metadata['interactive']);
  const isButtonReply = Boolean(metadata['button'])
    || Boolean(inboundInteractive?.['button_reply'])
    || Boolean(inboundInteractive?.['list_reply']);
  // Botões de uma mensagem NOSSA que os carrega (template de disparo ou mensagem interativa) —
  // chips somente-leitura; o WhatsApp já mostra os botões reais no aparelho do contato. Botão de
  // link vira chip clicável. Recebida nunca traz conjunto de botões (o contato manda um reply só).
  const buttons = mapMessageButtons(metadata);
  const storyContext = mapStoryContext(metadata);
  const quotedReply = mapQuotedReply((message as Message & { replyTo?: RawMessageReplyTo }).replyTo);
  // The story media is attached as a normal attachment (so the agent sees it), but
  // the UI renders it inside the story-context block — exclude it from the plain
  // attachment list to avoid rendering it twice.
  const storyFileId = storyContext?.attachments[0]?.fileId ?? null;
  // WhatsApp stickers arrive as image/webp, indistinguishable from a regular photo by mime
  // alone — the provider payload marks it explicitly (see meta.webhook-normalizer.ts).
  // `media` is flat on contentMetadata (same convention as storyReply/storyMention above) —
  // confirmed against production data, no `canonicalPayload` wrapper.
  const canonicalMedia = asRecord(metadata['media']);
  const isStickerMessage = canonicalMedia?.['isSticker'] === true;

  return {
    id: message.id,
    // Echoes (provider app) e disparos automáticos (campanha/webhook) são saídas nossas → lado
    // outbound, como uma mensagem de agente/membro (não centralizado como evento de sistema).
    sender: isEcho || isDispatch ? 'agent' : mapMessageSender(message.senderType),
    time: formatMessageTime(message.sentAt),
    sentAtIso: message.sentAt,
    body: message.contentText,
    attachments: (message.attachments ?? [])
      .filter((attachment) => attachment.fileId !== storyFileId)
      .map((attachment) => mapAttachment(attachment, isStickerMessage)),
    deliveryStatus: message.deliveryStatus ?? null,
    failureReason: message.failureReason ?? null,
    agentRunId: message.agentRunId ?? null,
    ...(isEcho && { isEcho: true }),
    ...(isCampaign && { isCampaign: true }),
    ...(isWebhook && { isWebhook: true }),
    ...(isButtonReply && { isButtonReply: true }),
    ...(buttons.length > 0 && { buttons }),
    ...(storyContext && { storyContext }),
    ...(quotedReply && { quotedReply }),
  };
}

function mapQuotedReply(raw: RawMessageReplyTo | undefined): ThreadQuotedReply | null {
  if (!raw) return null;
  if (raw.unavailable) return { unavailable: true };
  return {
    unavailable: false,
    messageId: raw.messageId,
    sender: raw.senderType ? mapMessageSender(raw.senderType) : undefined,
    preview: raw.preview,
  };
}

function mapStoryContext(metadata: Record<string, unknown>): ThreadStoryContext | null {
  const reply = asRecord(metadata['storyReply']);
  const mention = asRecord(metadata['storyMention']);
  const source = reply ? { kind: 'reply' as const, record: reply } : mention ? { kind: 'mention' as const, record: mention } : null;
  if (!source) return null;

  const url = typeof source.record['url'] === 'string' ? (source.record['url'] as string) : null;
  const fileId = typeof source.record['fileId'] === 'string' ? (source.record['fileId'] as string) : null;
  const contentType = typeof source.record['contentType'] === 'string' ? (source.record['contentType'] as string) : '';

  const attachments: ThreadAttachment[] = fileId
    ? [{ id: fileId, fileId, contentType, filename: null, sizeBytes: 0 }]
    : [];

  // Nothing renderable (neither a durable file nor a fallback url) → no context.
  if (!url && attachments.length === 0) return null;
  return { kind: source.kind, attachments, url };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

/**
 * Botões a exibir como chips de uma mensagem NOSSA: de um template de disparo
 * (`metadata.template.buttons`, com `text`/`url`) OU de uma mensagem interativa que enviamos
 * (`metadata.interactive.buttons`, com `label`, sem link). Recebida não cai aqui: o inbound
 * interativo traz `button_reply`/`list_reply` (um reply só), nunca um array `buttons`.
 */
function mapMessageButtons(metadata: Record<string, unknown>): ThreadMessageButton[] {
  const fromTemplate = extractButtons(asRecord(metadata['template'])?.['buttons']);
  if (fromTemplate.length > 0) return fromTemplate;
  return extractButtons(asRecord(metadata['interactive'])?.['buttons']);
}

/** Normaliza uma lista crua de botões para chips: aceita `text` (template) ou `label` (interativo);
 *  `url` presente → chip clicável (`href`). Ignora entradas sem rótulo. */
function extractButtons(raw: unknown): ThreadMessageButton[] {
  if (!Array.isArray(raw)) return [];
  const result: ThreadMessageButton[] = [];
  for (const item of raw) {
    const b = asRecord(item);
    const label = b?.['text'] ?? b?.['label'];
    if (typeof label !== 'string' || label.trim() === '') continue;
    const url = b?.['url'];
    result.push(typeof url === 'string' && url.trim() !== '' ? { text: label, href: url } : { text: label });
  }
  return result;
}

function mapAttachment(attachment: MessageAttachment, isSticker = false): ThreadAttachment {
  return {
    id: attachment.id,
    fileId: attachment.fileId,
    contentType: attachment.contentType,
    filename: attachment.filename ?? null,
    sizeBytes: attachment.sizeBytes,
    signedUrl: attachment.signedUrl ?? null,
    placeholder: attachment.placeholder ?? null,
    width: attachment.width ?? null,
    height: attachment.height ?? null,
    analysisStatus: attachment.analysisStatus ?? null,
    ...(isSticker && { isSticker: true }),
  };
}

function mapMessageSender(senderType: Message['senderType']): MessageSenderGroup {
  if (senderType === 'contact') {
    return 'contact';
  }

  if (senderType === 'system') {
    return 'system';
  }

  return 'agent';
}

function formatMessageTime(value: string): string {
  return new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(new Date(value));
}
